import type { TrackingAdapterSummary, TrackingConnectionResult } from '@chukua/common';
import { useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { installIntegration, testIntegrationConnection, updateIntegration } from '../integrations.api';
import { apiErrorReason } from '../policies/api-error-reason.policy';
import { toConfigPayload, type ConfigFormValues } from '../policies/config-form-values.policy';
import type { AdapterCatalogueState } from './use-adapter-catalogue';
import { useSavedIntegration } from './use-saved-integration';

export interface IntegrationFormValues extends ConfigFormValues {
  displayName: string;
  pollingIntervalSeconds?: number | null;
}

interface UseIntegrationSheetArgs {
  catalogue: AdapterCatalogueState;
  /** The adapter being installed (`?adapter=`), or undefined when editing. */
  adapterKey: string | undefined;
  /** The installation being edited, or undefined when installing. */
  integrationId: string | undefined;
  onDone: () => void;
}

/** Install or edit one installation: load what is being edited, test the connection, save. Failures stay inline next to the form (CLAUDE.md → Feedback). */
export function useIntegrationSheet({ catalogue, adapterKey, integrationId, onDone }: UseIntegrationSheetArgs) {
  const toast = useToast();
  const saved = useSavedIntegration(integrationId);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TrackingConnectionResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const integration = saved.status === 'loaded' ? saved.integration : undefined;
  const wantedKey = adapterKey ?? integration?.adapterKey;
  const adapter: TrackingAdapterSummary | undefined =
    catalogue.status === 'loaded' ? catalogue.adapters.find((candidate) => candidate.key === wantedKey) : undefined;
  const isLoading = (integrationId ? saved.status === 'loading' : false) || catalogue.status === 'loading';

  async function test(values: IntegrationFormValues): Promise<void> {
    if (!adapter) return;
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await testIntegrationConnection({ adapterKey: adapter.key, config: toConfigPayload(adapter.fields, values), integrationId }));
    } catch (err) {
      setTestResult({ ok: false, reason: apiErrorReason(err, 'The test could not be run') });
    } finally {
      setTesting(false);
    }
  }

  async function save(values: IntegrationFormValues): Promise<void> {
    if (!adapter) return;
    setSubmitting(true);
    setError(null);
    const config = toConfigPayload(adapter.fields, values);
    const pollingIntervalSeconds = adapter.capabilities.includes('polling') ? values.pollingIntervalSeconds : undefined;
    try {
      if (integrationId) await updateIntegration(integrationId, { displayName: values.displayName, config, pollingIntervalSeconds });
      else await installIntegration({ adapterKey: adapter.key, displayName: values.displayName, config, pollingIntervalSeconds });
      toast.success(integrationId ? `${values.displayName} updated` : `${values.displayName} installed`);
      onDone();
    } catch (err) {
      setError(apiErrorReason(err, 'Could not save the integration. Check the fields and try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return {
    adapter,
    integration,
    isLoading,
    loadFailed: saved.status === 'error' || catalogue.status === 'error',
    testing,
    testResult,
    submitting,
    error,
    test,
    save,
  };
}
